import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import apiService from '../../services/api';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function ReviewTemplateManager({ token }) {
  const [templates, setTemplates] = useState([]);
  const [reviewTypes, setReviewTypes] = useState([]);
  const [reviewerPersonas, setReviewerPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    templateKey: '',
    name: '',
    description: '',
    reviewType: 'safety_review',
    reviewTypeId: '',
    systemPrompt: '',
    allowCustomInstructions: true,
    temperature: 0.3,
    maxTokens: 8192,
    isActive: true,
    reviewerPersonaId: ''
  });

  useEffect(() => {
    loadTemplates();
    loadReviewTypes();
    loadReviewerPersonas();
  }, [token]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/review-templates?includeInactive=true');
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadReviewTypes = async () => {
    try {
      const response = await apiService.get('/review-types/all');
      setReviewTypes(response.data.data || []);
    } catch (err) {
      console.error('Error loading review types:', err);
    }
  };

  const loadReviewerPersonas = async () => {
    try {
      const response = await apiService.get('/review-templates/reviewer-personas');
      setReviewerPersonas(response.data.personas || []);
    } catch (err) {
      console.error('Error loading reviewer personas:', err);
    }
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    setFormData({
      templateKey: '',
      name: '',
      description: '',
      reviewType: 'safety_review',
      reviewTypeId: reviewTypes.find(rt => rt.typeKey === 'safety_review')?._id || '',
      systemPrompt: '',
      allowCustomInstructions: true,
      temperature: 0.3,
      maxTokens: 8192,
      isActive: true,
      reviewerPersonaId: ''
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      templateKey: template.templateKey,
      name: template.name,
      description: template.description,
      reviewType: template.reviewType,
      reviewTypeId: template.reviewTypeId?._id || template.reviewTypeId || '',
      systemPrompt: template.systemPrompt,
      allowCustomInstructions: template.allowCustomInstructions,
      temperature: template.llmConfig?.temperature || 0.3,
      maxTokens: template.llmConfig?.maxTokens || 8192,
      isActive: template.isActive,
      reviewerPersonaId: template.reviewerPersonaId?._id || template.reviewerPersonaId || ''
    });
    setEditDialogOpen(true);
  };

  const handleView = (template) => {
    setSelectedTemplate(template);
    setViewDialogOpen(true);
  };

  const handleDuplicate = async (template) => {
    try {
      const newKey = prompt('Enter new template key:', `${template.templateKey}_copy`);
      if (!newKey) return;

      const newName = prompt('Enter new template name:', `${template.name} (Copy)`);
      if (!newName) return;

      await apiService.post(
        `/review-templates/${template._id}/duplicate`,
        { newTemplateKey: newKey, newName }
      );

      setSuccess('Template duplicated successfully');
      loadTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to duplicate template');
    }
  };

  const handleSave = async () => {
    try {
      setError('');

      if (selectedTemplate) {
        // Update existing template
        await apiService.put(
          `/review-templates/${selectedTemplate._id}`,
          {
            name: formData.name,
            description: formData.description,
            reviewType: formData.reviewType,
            reviewTypeId: formData.reviewTypeId || null,
            systemPrompt: formData.systemPrompt,
            allowCustomInstructions: formData.allowCustomInstructions,
            isActive: formData.isActive,
            reviewerPersonaId: formData.reviewerPersonaId || null,
            llmConfig: {
              temperature: parseFloat(formData.temperature),
              maxTokens: parseInt(formData.maxTokens)
            }
          }
        );
        setSuccess('Template updated successfully');
      } else {
        // Create new template
        await apiService.post(
          '/review-templates',
          {
            templateKey: formData.templateKey,
            name: formData.name,
            description: formData.description,
            reviewType: formData.reviewType,
            reviewTypeId: formData.reviewTypeId || null,
            systemPrompt: formData.systemPrompt,
            allowCustomInstructions: formData.allowCustomInstructions,
            reviewerPersonaId: formData.reviewerPersonaId || null,
            llmConfig: {
              temperature: parseFloat(formData.temperature),
              maxTokens: parseInt(formData.maxTokens)
            }
          }
        );
        setSuccess('Template created successfully');
      }

      setEditDialogOpen(false);
      loadTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await apiService.put(
        `/review-templates/${template._id}`,
        { isActive: !template.isActive }
      );
      setSuccess(`Template ${template.isActive ? 'deactivated' : 'activated'} successfully`);
      loadTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update template');
    }
  };

  const getReviewTypeLabel = (type) => {
    const labels = {
      safety_review: 'Safety Screening',
      modality_fidelity: 'Modality Fidelity',
      keyword_search: 'Keyword Search',
      progress_assessment: 'Progress Assessment'
    };
    return labels[type] || type;
  };

  const templatesByType = templates.reduce((acc, template) => {
    if (!acc[template.reviewType]) {
      acc[template.reviewType] = [];
    }
    acc[template.reviewType].push(template);
    return acc;
  }, {});

  // Use review types from API, fallback to legacy type keys for tabs
  const tabReviewTypes = reviewTypes.length > 0
    ? reviewTypes.map(rt => ({ key: rt.typeKey, label: rt.name }))
    : [
        { key: 'safety_review', label: 'Safety Screening' },
        { key: 'modality_fidelity', label: 'Modality Fidelity' },
        { key: 'keyword_search', label: 'Keyword Search' },
        { key: 'progress_assessment', label: 'Progress Assessment' }
      ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Review Template Manager</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create New Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="All Templates" />
          {tabReviewTypes.map((type) => (
            <Tab key={type.key} label={type.label} />
          ))}
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Custom Instructions</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template._id}>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {template.templateKey}
                      </Typography>
                    </TableCell>
                    <TableCell>{getReviewTypeLabel(template.reviewType)}</TableCell>
                    <TableCell>v{template.version}</TableCell>
                    <TableCell>
                      <Chip
                        label={template.allowCustomInstructions ? 'Allowed' : 'Not Allowed'}
                        color={template.allowCustomInstructions ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={template.isActive ? 'Active' : 'Inactive'}
                        color={template.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleView(template)} title="View">
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleEdit(template)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDuplicate(template)} title="Duplicate">
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(template)}
                        title={template.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {tabReviewTypes.map((type, idx) => (
          <TabPanel key={type.key} value={tabValue} index={idx + 1}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Custom Instructions</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(templatesByType[type.key] || []).map((template) => (
                    <TableRow key={template._id}>
                      <TableCell>{template.name}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {template.templateKey}
                        </Typography>
                      </TableCell>
                      <TableCell>v{template.version}</TableCell>
                      <TableCell>
                        <Chip
                          label={template.allowCustomInstructions ? 'Allowed' : 'Not Allowed'}
                          color={template.allowCustomInstructions ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.isActive ? 'Active' : 'Inactive'}
                          color={template.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleView(template)} title="View">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleEdit(template)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDuplicate(template)} title="Duplicate">
                          <CopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(template)}
                          title={template.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        ))}
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTemplate ? 'Edit Template' : 'Create New Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Template Key"
              value={formData.templateKey}
              onChange={(e) => setFormData({ ...formData, templateKey: e.target.value })}
              disabled={!!selectedTemplate}
              helperText="Unique identifier (e.g., safety_screening_v1)"
              required
            />

            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Review Type</InputLabel>
              <Select
                value={formData.reviewTypeId}
                onChange={(e) => {
                  const selectedType = reviewTypes.find(rt => rt._id === e.target.value);
                  setFormData({
                    ...formData,
                    reviewTypeId: e.target.value,
                    reviewType: selectedType?.typeKey || formData.reviewType
                  });
                }}
                label="Review Type"
              >
                {reviewTypes.length > 0 ? (
                  reviewTypes.map(rt => (
                    <MenuItem key={rt._id} value={rt._id}>
                      {rt.name} {rt.isSystemType ? '(System)' : ''}
                    </MenuItem>
                  ))
                ) : (
                  <>
                    <MenuItem value="">Loading...</MenuItem>
                    <MenuItem value="safety_review">Safety Screening (Legacy)</MenuItem>
                    <MenuItem value="modality_fidelity">Modality Fidelity (Legacy)</MenuItem>
                    <MenuItem value="keyword_search">Keyword Search (Legacy)</MenuItem>
                    <MenuItem value="progress_assessment">Progress Assessment (Legacy)</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>

            {formData.reviewTypeId && (
              <Alert severity="info" sx={{ mt: -1 }}>
                This template will inherit context configuration and reference documents from the selected Review Type.
              </Alert>
            )}

            <TextField
              fullWidth
              multiline
              rows={12}
              label="System Prompt"
              value={formData.systemPrompt}
              onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
              helperText="The LLM prompt that defines how this review should be conducted"
              required
            />

            <FormControl fullWidth>
              <InputLabel>Therapy Reviewer Persona (Optional)</InputLabel>
              <Select
                value={formData.reviewerPersonaId || ''}
                onChange={(e) => setFormData({ ...formData, reviewerPersonaId: e.target.value })}
                label="Therapy Reviewer Persona (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                {reviewerPersonas.map(persona => (
                  <MenuItem key={persona._id} value={persona._id}>
                    {persona.name} ({persona.key})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.reviewerPersonaId && (() => {
              const persona = reviewerPersonas.find(p => p._id === formData.reviewerPersonaId);
              return persona ? (
                <Alert severity="info" sx={{ mt: -1 }}>
                  <Typography variant="body2" gutterBottom>
                    This template will use <strong>{persona.name}</strong>'s system prompt and evaluation dimensions. The template's own system prompt will be kept as fallback.
                  </Typography>
                  {persona.reviewConfiguration?.evaluationDimensions?.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                      {persona.reviewConfiguration.evaluationDimensions.map((dim, idx) => (
                        <Chip
                          key={idx}
                          label={dim.name?.replace(/_/g, ' ')}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </Alert>
              ) : null;
            })()}

            <Divider />

            <Typography variant="subtitle2" gutterBottom>
              LLM Configuration
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Temperature"
                type="number"
                value={formData.temperature}
                onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                sx={{ flex: 1 }}
              />

              <TextField
                label="Max Tokens"
                type="number"
                value={formData.maxTokens}
                onChange={(e) => setFormData({ ...formData, maxTokens: e.target.value })}
                inputProps={{ min: 1000, max: 32000, step: 1024 }}
                sx={{ flex: 1 }}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.allowCustomInstructions}
                  onChange={(e) => setFormData({ ...formData, allowCustomInstructions: e.target.checked })}
                />
              }
              label="Allow Custom Instructions"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {selectedTemplate ? 'Save Changes' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>View Template</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Template Key:</Typography>
              <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1 }}>
                {selectedTemplate.templateKey}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>Name:</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{selectedTemplate.name}</Typography>

              <Typography variant="subtitle2" gutterBottom>Description:</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{selectedTemplate.description}</Typography>

              <Typography variant="subtitle2" gutterBottom>Review Type:</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{getReviewTypeLabel(selectedTemplate.reviewType)}</Typography>

              <Typography variant="subtitle2" gutterBottom>Version:</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>v{selectedTemplate.version}</Typography>

              <Typography variant="subtitle2" gutterBottom>System Prompt:</Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {selectedTemplate.systemPrompt}
                </Typography>
              </Paper>

              {selectedTemplate.reviewerPersonaId && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Therapy Reviewer Persona:</Typography>
                  <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                      label={`${selectedTemplate.reviewerPersonaId.name || 'Unknown'} (${selectedTemplate.reviewerPersonaId.key || ''})`}
                      color="primary"
                      variant="outlined"
                    />
                    {selectedTemplate.reviewerPersonaId.reviewConfiguration?.evaluationDimensions?.map((dim, idx) => (
                      <Chip key={idx} label={dim.name?.replace(/_/g, ' ')} size="small" variant="outlined" />
                    ))}
                  </Box>
                </>
              )}

              <Typography variant="subtitle2" gutterBottom>LLM Configuration:</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Temperature: {selectedTemplate.llmConfig?.temperature || 0.3},
                Max Tokens: {selectedTemplate.llmConfig?.maxTokens || 8192}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>Settings:</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  label={selectedTemplate.allowCustomInstructions ? 'Custom Instructions Allowed' : 'No Custom Instructions'}
                  color={selectedTemplate.allowCustomInstructions ? 'success' : 'default'}
                />
                <Chip
                  label={selectedTemplate.isActive ? 'Active' : 'Inactive'}
                  color={selectedTemplate.isActive ? 'success' : 'default'}
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                Created: {new Date(selectedTemplate.createdAt).toLocaleString()}<br />
                Updated: {new Date(selectedTemplate.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button onClick={() => {
            setViewDialogOpen(false);
            handleEdit(selectedTemplate);
          }} variant="contained">
            Edit Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ReviewTemplateManager;
